<script setup lang="ts">
import ProductList from '~/components/plp/ProductList.vue';
import FilterBar from '~/components/filter-bar/index.vue';
import { useRoute } from 'vue-router';
import {
	fetchCollectionProductCount,
	fetchCollectionProducts,
	useProductsCount,
	useProductsList,
} from '~/data/products';
import Description from '~/components/plp/Description.vue';
import { Separator } from 'radix-vue';
import SeoProvider from '~/components/plp/SeoProvider.vue';
import type { FeedNode } from '~/components/plp';
import type { SeoData } from '~/data/entities';
import NodesBar from '~/components/plp/NodesBar.vue';
import WebPageSeo from '~/components/seo/WebPageSeo.vue';

const { $cathodeClient } = useNuxtApp();
const route = useRoute();
const { storeType } = useGlobalStore();

const routeName = 'collections-handle';
const handle = route.params.handle as string;

const filters = useFilterParams(routeName);

const count = useProductsCount({
	routeName,
	queryKey: ['collections', handle, filters],
	queryFn: () => fetchCollectionProductCount(handle, filters.value),
});

const allowSSR = true;
const {
	data,
	isLoading,
	hasNextPage,
	fetchNextPage,
	suspense,
	isFetchingNextPage,
	error: productListError,
} = useProductsList({
	routeName,
	queryKey: ['collections', handle, filters],
	queryFn: ({ pageParam: cursor }) => {
		return fetchCollectionProducts(handle, cursor as string, filters.value);
	},
	allowSSR,
});

onServerPrefetch(async () => {
	await suspense();
});

watch(isLoading, (isLoading) => {
	if (!isLoading && productListError.value) {
		throw createError({
			statusCode: 404,
			message: 'Failed to load products, route ' + route.fullPath,
			fatal: true,
		});
	}
});

onMounted(() => {
	$cathodeClient.queueEvent('page_load', {
		source: `${window.location.origin}${route.path}`,
		page: 'PLP',
		statusCode: 200,
		referrer: document.referrer,
		collection_handle: route.params.handle as string,
	});
});

const shouldShowRelatedCollections = (seo?: SeoData) => {
	if (!seo) return false;
	if ('related_collections' in seo && seo.related_collections) {
		return seo.related_collections.length > 0;
	}
	return false;
};

const getRelatedCollections = (seo?: SeoData): FeedNode[] => {
	if (!seo) return [];
	if ('related_collections' in seo && seo.related_collections) {
		return seo.related_collections.map((collection, idx) => ({
			id: idx,
			title: collection.title,
			href: 'collections/' + collection.handle,
			image: '',
			logo: '',
			node_level: 0,
			parent_id: 0,
			product_count: 0,
			valhalla_score: 0,
			path: [],
		}));
	}
	return [];
};
</script>
<template>
	<SeoProvider v-slot="{ seo }" :handle="handle" :route-name="routeName">
		<WebPageSeo
			:data="seo"
			:item-list-data="data?.pages"
			:handle="handle"
			:count="count"
		/>
		<div>
			<PlpHeader :data="seo" :items-count="count" />
			<div
				v-if="shouldShowRelatedCollections(seo)"
				class="flex flex-col gap-3xl border-b border-gray-200 py-3xl"
			>
				<NodesBar :show-new="false" :feed-nodes="getRelatedCollections(seo)" />
			</div>
			<ClientOnly>
				<FilterBar :handle="handle" :is-product-list-loading="isLoading" />
				<template #fallback>
					<FilterBarSkeleton />
				</template>
			</ClientOnly>
			<ProductList
				:infinite-loader="true"
				:fetch-next-page="fetchNextPage"
				:is-loading="isLoading"
				:data="data"
				:has-next-page="hasNextPage"
				:is-fetching-next-page="isFetchingNextPage"
			/>

			<div
				v-if="
					!!seo?.description &&
					!!seo?.title &&
					storeType === STORE_TYPE.MARKETPLACE
				"
				class="w-full"
			>
				<Separator class="seo-description-separator" />
				<Description :description="seo.description" :heading="seo.title" />
			</div>
		</div>
	</SeoProvider>
</template>

<style scoped>
.seo-description-separator {
	@apply !bg-gray-100 h-xl mt-5xl;
}
</style>
